'use client';

import { useEffect, useLayoutEffect, useRef } from 'react';

import { useAppStore } from '@/shared/contexts/app-wrapper/providers/app-provider';

import { AgentAppearanceProvider } from '../../contexts/agent-appearance-context';
import { useAgentChatState } from '../../contexts/agent-chat-provider';
import { AgentPreviewProvider } from '../../contexts/agent-preview-provider';
import { readLastChatForWorkspace, useAgentStore } from '../../contexts/agent-provider';
import {
	AGENT_SURFACES,
	setAgentOnboardingSession,
	setAgentSurface,
} from '../../utils/agent-analytics';
import { takeAgentUrlPrompt } from '../../utils/agent-utils';
import { AgentChatView } from './agent-chat-view';

type FullPageAgentProps = {
	/** When true, opens the agent-initiated onboarding opener once — but only if this
	 *  workspace has no prior chat (so a refresh restores the chat instead of duplicating it). */
	autoStartOnboarding?: boolean;
};

/**
 * Full-page agent surface for `/ai`. Reuses the shared chat view (header + message
 * list + input) inside the ambient AgentProvider from the dashboard.
 *
 * It's the single surface for the route: with `autoStartOnboarding` it brands the stack
 * and opens the agent-initiated first-run opener once; without it, it's the normal agent
 * (user types first, last chat restored). One surface either way — no reactive
 * onboarding↔normal swap, so nothing remounts mid-chat.
 */
export function FullPageAgent({ autoStartOnboarding = false }: FullPageAgentProps) {
	const { startOnboarding } = useAgentChatState();
	const selectedWorkspaceId = useAppStore((state) => state.selectedWorkspaceId);
	const setPendingUrlPrompt = useAgentStore((s) => s.setPendingUrlPrompt);
	const setOnboardingOpener = useAgentStore((s) => s.setOnboardingOpener);
	const startedRef = useRef(false);

	// Arm the opener filler before first paint — the start effect below waits for
	// workspaceId, and the empty-state suggestions must not flash in the meantime.
	// Disarmed again below if a restored chat means no opener runs this session.
	useLayoutEffect(() => {
		if (autoStartOnboarding) setOnboardingOpener({ started: true });
	}, [autoStartOnboarding, setOnboardingOpener]);

	// Tags every agent event with `location: full_page` while this surface is mounted.
	useEffect(() => {
		setAgentSurface(AGENT_SURFACES.FULL_PAGE);
		return () => setAgentSurface(AGENT_SURFACES.SIDE_PANEL);
	}, []);

	// Flags the onboarding session for every agent event's `is_onboarding`.
	useEffect(() => {
		if (!autoStartOnboarding) return;
		setAgentOnboardingSession(true);
		return () => setAgentOnboardingSession(false);
	}, [autoStartOnboarding]);

	// Stash `?prompt=` for auto-send (useAgentChat consumes it); skipped for onboarding, whose opener would race it into two chats.
	useEffect(() => {
		if (autoStartOnboarding) return;
		const urlPrompt = takeAgentUrlPrompt();
		if (urlPrompt) setPendingUrlPrompt(urlPrompt);
	}, [autoStartOnboarding, setPendingUrlPrompt]);

	useEffect(() => {
		if (!autoStartOnboarding || startedRef.current || !selectedWorkspaceId) return;
		startedRef.current = true;
		// The opener is agent-initiated, so it's always the onboarding opener (true). Skip if a
		// chat already exists for this workspace — the provider restores it, so we'd otherwise
		// open a second one on refresh (child effect runs before the provider's restore effect).
		if (readLastChatForWorkspace(selectedWorkspaceId)) {
			// No opener this session — disarm so the filler can't attach to any chat.
			setOnboardingOpener({ started: false });
			return;
		}
		startOnboarding(true);
	}, [autoStartOnboarding, selectedWorkspaceId, startOnboarding, setOnboardingOpener]);

	return (
		<AgentAppearanceProvider
			fullPage
			onboarding={autoStartOnboarding}
		>
			<AgentPreviewProvider>
				<div className="relative flex h-full w-full flex-col overflow-hidden bg-slate-50 antialiased">
					{autoStartOnboarding && (
						<div
							aria-hidden
							className="pointer-events-none absolute inset-x-0 top-0 h-64 bg-[radial-gradient(60%_100%_at_50%_0%,theme(colors.blue.100/0.7),transparent_70%)]"
						/>
					)}
					<div className="relative z-10 flex w-full flex-1 flex-col overflow-hidden">
						<AgentChatView />
					</div>
				</div>
			</AgentPreviewProvider>
		</AgentAppearanceProvider>
	);
}
